<script lang="ts" setup>
import { z } from 'zod'

const props = defineProps({
  apiToken: {
    required: true,
    type: Object as PropType<ApiToken>,
  },
  removable: {
    default: true,
    type: Boolean,
  },
})

const { createEvent } = useAnalytics()
const { createToast } = useToasts()
const { t } = useI18n()
const teamsStore = useTeamsStore()

const removing = ref(false)
const updating = ref(false)
const form = reactive({ name: props.apiToken?.name || '' })

const validation = z.object({
  name: z.string()
    .min(1, { message: t('validation.required') })
    .max(255, { message: t('validation.max', { length: 255 }) }),
})

const { isFormUpdated, saveFormState } = useFormState(form)

async function onRemoveApiToken() {
  removing.value = true

  try {
    await teamsStore.removeTeamApiToken({ apiTokenId: props.apiToken.id })

    createEvent({ name: '[Settings] Remove Team API Token' })
    createToast({ message: 'teamApiTokenRemoved' })
  }
  catch (error) {
    console.error(error)
  }

  removing.value = false
}

async function onUpdateApiToken() {
  if (!updating.value && isFormUpdated.value && form.name) {
    updating.value = true

    try {
      await teamsStore.updateTeamApiToken({
        apiTokenId: props.apiToken.id,
        name: form.name,
      })

      saveFormState()
      createEvent({ name: '[Settings] Update Team API Token' })
      createToast({ message: 'teamApiTokenUpdated' })
    }
    catch (error) {
      console.error(error)
    }
  }

  updating.value = false
}
</script>

<template>
  <UForm
    :schema="validation"
    :state="form"
    @submit="onUpdateApiToken"
  >
    <li class="flex gap-2">
      <div class="flex flex-1 items-center gap-2">
        <UFormGroup
          class="w-full"
          name="name"
        >
          <UInput
            v-model="form.name"
            class="w-full"
            :icon="getIcon('apiToken')"
            :loading="updating"
            :placeholder="t('tokenName')"
            @blur="onUpdateApiToken"
          />
        </UFormGroup>
      </div>

      <div class="flex items-start gap-2">
        <ButtonClipboard
          class="sm:hidden"
          :has-label="false"
          size="md"
          :string-to-copy="apiToken.token"
        />

        <ButtonClipboard
          class="hidden sm:inline-flex"
          :string-to-copy="apiToken.token"
        />

        <UTooltip
          v-if="removable"
          :text="t('removeToken')"
        >
          <UButton
            :icon="getIcon('trash')"
            :loading="removing"
            size="md"
            :ui="{ icon: { base: 'text-red-600 dark:text-red-400' } }"
            @click="onRemoveApiToken"
          />
        </UTooltip>
      </div>
    </li>
  </UForm>
</template>

<i18n lang="json">
{
  "en": {
    "removeToken": "Remove Token",
    "tokenName": "Token Name"
  },
  "es": {
    "removeToken": "Eliminar token",
    "tokenName": "Nombre del token"
  },
  "fr": {
    "removeToken": "Supprimer le token",
    "tokenName": "Nom du token"
  }
}
</i18n>
